import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, Users, ArrowRightIcon, MenuIcon, XIcon, MoonIcon } from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import { CourseCalendar } from '../components/CourseCalendar';
import { HeroRotator } from '../components/HeroRotator';
import { Logo } from '../components/Logo';
import { currentCourse } from '../data/courses';
// ═══════════════════════════════════════════════════════════
// 🔗 REGISTRACIJOS NUORODA — čia įrašykite savo anketos URL
// ═══════════════════════════════════════════════════════════
const REGISTRATION_URL = 'https://forms.gle/RU4dDRpzfxv5AceV6';
export function LandingPage({
  mode,
  toggleMode,
  onViewCourses
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  onViewCourses: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialDirection, setTestimonialDirection] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-130" data-mp="355sssn979pcp">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-131" data-mp="356cdgbq8iruf">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-132" data-mp="3e7tq1k4fobed" />
    </div>;
  return <main className="w-full bg-sand-lightest transition-colors duration-300" data-id="element-133" data-mp="397nx80mfgiup">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: 'rgba(248, 243, 238, 0.92)',
      backdropFilter: 'blur(12px)',
      boxShadow: scrolled ? '0 1px 0 rgba(27, 27, 75, 0.10)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-134" data-mp="3o4evu7oa1qj9">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-135" data-mp="5sifitmkgsif">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-136" data-mp="pao1b5sszf9e">
            <Logo className="h-9 md:h-10" data-id="element-137" data-mp="uaitdcobeaw1" />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-138" data-mp="fdjdz2n81dr0">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className="text-sm font-medium uppercase tracking-wider transition-colors duration-200 text-[#1B1B4B]/70 hover:text-[#1B1B4B]" data-id="element-139" data-mp="3jtaz1q2eus18">
                {link.label}
              </a>)}
          </div>

          {/* CTA & Dark Mode */}
          <div className="hidden md:flex items-center gap-4" data-id="element-140" data-mp="370v3r8bbz8cv">
            <button onClick={toggleMode} className="p-2 rounded-full transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10" aria-label="Toggle dark mode" data-id="element-141" data-mp="36za8thl3tneh">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-142" data-mp="2t00tsl8mwzc3" /> : <SunIcon className="w-5 h-5" data-id="element-143" data-mp="2o1pjk9efgmtc" />}
            </button>
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-144" data-mp="2j3o03nd3alhc">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-145" data-mp="yngfx4aq5ywi">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-146" data-mp="13josqb1g9g0i" />
              </svg>
              Parašyk mums
            </motion.a>
          </div>

          {/* Mobile burger & Dark Mode */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-147" data-mp="nuida7q0gmy4">
            <button onClick={toggleMode} className="p-2 rounded-full transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B] hover:bg-[#1B1B4B]/10" aria-label="Toggle dark mode" data-id="element-148" data-mp="nv0k1yaaujka">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-149" data-mp="ssk5bi5ezwlq" /> : <SunIcon className="w-5 h-5" data-id="element-150" data-mp="53r6ndxeranf" />}
            </button>
            <button className="p-1 transition-colors text-[#1B1B4B]/80 hover:text-[#1B1B4B]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-151" data-mp="51o7o4ea276k">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-152" data-mp="2gk1gt45ifxii" /> : <MenuIcon className="w-6 h-6" data-id="element-153" data-mp="21u3qahk3yvq5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-154" data-mp="26u82vwhwb6b3">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: '#1e0664'
        }} data-id="element-155" data-mp="2uird4hki9t5t">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-156" data-mp="1e5kdboq45urq">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium uppercase tracking-wider py-1 transition-colors" data-id="element-157" data-mp="2fo5n64aiixt1">
                    {link.label}
                  </a>)}
                <a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" style={{
              background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
            }} data-id="element-158" data-mp="3ask1b1ho4u15">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-159" data-mp="2h7ge9gsq6am">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-160" data-mp="c8txgmftezla" />
                  </svg>
                  Parašyk mums
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO (rotuojamas) ── */}
      <HeroRotator data-id="element-161" data-mp="ejxrf9ztx2ep" />

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-162" data-mp="jg60q6rci1dy">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-163" data-mp="jish8vw7rr8b">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed font-[300]" data-id="element-164" data-mp="2wsijepprfpvw">
            Ne visada nuo tavęs priklauso, kas nutiks tavo gyvenime, tačiau gali sąmoningai spręsti, kaip reaguosi į tai, kas vyksta. Šis kursas padės geriau pažinti savo reakcijas, valdyti dėmesį ir sąmoningai pasirinkti būseną, kurioje nori būti.

          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-165" data-mp="3fnqqpkqoig3z">
        <div className="max-w-6xl mx-auto" data-id="element-166" data-mp="3bpt2ol8na026">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-167" data-mp="36peu9234so3m">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-168" data-mp="36ntyxa8zt14p">
              Visiems Pažįstami
            </p>
            <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-169" data-mp="31selukit799c" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-170" data-mp="1rtdk0i64e4mg">
              Kasdieniai Iššūkiai
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-171" data-mp="1wp16uqx4qlos">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-172" data-mp="1dsj9ntzihtl5">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?w=600&q=80')"
            }} data-id="element-173" data-mp="142q171uo1ul4" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-174" data-mp="v3w65fwzhc9o" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-175" data-mp="lg0n9fdbf67o">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-176" data-mp="pfraj93mv47g">
                <p className="text-lg font-bold text-white mb-1" data-id="element-177" data-mp="143vv6im8xpsv">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-178" data-mp="z6m8vbu5lu99">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-179" data-mp="32mqv71u3j1lf">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fkDNhwZwL2Fman3fp7TuCW/moritz-kindler-n6XW1P6a-_U-unsplash.jpg')"
            }} data-id="element-180" data-mp="3ccsms3lnr253" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-181" data-mp="2omg5w38fpv2d" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-182" data-mp="2xlbchgfe5767">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-183" data-mp="1tdurlmbmwagt">
                <p className="text-lg font-bold text-white mb-1" data-id="element-184" data-mp="1tfz24n41g7q2">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-185" data-mp="1jo2pm717gf2b">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-186" data-mp="19q543raqcwde">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1669369172813-938c5aaa8685?w=600&q=80')"
            }} data-id="element-187" data-mp="10pqfoj2gz15o" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-188" data-mp="3e7uvei0bg923" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-189" data-mp="358zo12ljcsx7">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-190" data-mp="5pxa7j09hujp">
                <p className="text-lg font-bold text-white mb-1" data-id="element-191" data-mp="3nwuz2eprjsmm">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-192" data-mp="3svy07o2mhepb">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-193" data-mp="2hhu9cqrc06oh">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516490981167-dc990a242afe?w=600&q=80')"
            }} data-id="element-194" data-mp="24xx0rokg0ku4" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-195" data-mp="1q9ke5i78weca" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-196" data-mp="201q5qqtl4tqy">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-197" data-mp="hyxpxbwdmujt">
                <p className="text-lg font-bold text-white mb-1" data-id="element-198" data-mp="i0qywxugpquz">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-199" data-mp="m116gexwkpby">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-200" data-mp="10wqsgd3392go">
        <div className="max-w-6xl mx-auto" data-id="element-201" data-mp="14ylrtc1gl5rt">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-202" data-mp="14xapzhrkq83c">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-203" data-mp="19pkectd1j03i">
              Paieškos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-204" data-mp="1enlvv9utircv" />
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-205" data-mp="395ommztspy95">
              Tarp nuolatinės kaitos ir pusiausvyros paieškų
            </h2>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-206" data-mp="2z6zazoshta1c">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-207" data-mp="3mwjwofg16leb">
              <p className="text-[20px] text-white/80 font-[300]" data-id="element-208" data-mp="3i51wvupququx">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-209" data-mp="20am8a8vuu2av">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-210" data-mp="2j36n4mepy6vo" data-mpi={i}>
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-211" data-mp="259p370i4es54">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-212" data-mp="3a8ubxynnhpr8">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-213" data-mp="3j7fond6fyak3">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-214" data-mp="3t5vihnwurxxa" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-215" data-mp="avbhw21rx3gh" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-216" data-mp="27vebrdwftizp">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-217" data-mp="2gvb5htxop81m">
            <div className="max-w-xl" data-id="element-218" data-mp="2gsygiwtidxf2">
              {/* Italic subtitle — now above */}
              <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-219" data-mp="2vi4dsf8sddkb">
                Sprendimas
              </p>
              {/* Gold line */}
              <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
              background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
            }} data-id="element-220" data-mp="34kvr6kl82jel" />
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-221" data-mp="158kutigh6jpq">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Paragraphs */}
              <p className="text-white/80 mb-5 font-[300] text-[20px]" data-id="element-222" data-mp="3lu7r87ddnjyt">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-white/80 font-[300] text-[20px]" data-id="element-223" data-mp="2y43xcrpzqyl0">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── (dark bg) */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-224" data-mp="2t5kberbedmsn">
        <div className="max-w-6xl mx-auto" data-id="element-225" data-mp="nddiz9zwg248">
          <motion.div className="text-center mb-10" {...fadeInUp} data-id="element-226" data-mp="na7x03mkhgcq">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-227" data-mp="rcmfgmmbqe3h">
              Aktyvios Savireguliacijos Sistema
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-228" data-mp="2kx46j5io8oc" />
            <h3 className="text-3xl md:text-4xl font-bold text-white" data-id="element-229" data-mp="3juv8f8joqfgp">
              16 Funkcinių Būsenų
            </h3>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-230" data-mp="3b3cul4a7obqs">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-231" data-mp="3b10422qe0t24">
              <p className="text-white/80 leading-relaxed" data-id="element-232" data-mp="361nkpryq644a">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena. Trumpų distancijų bėgimas reikalauja
                kitokio pasirengimo nei maratonas. Lengvas pokalbis – tai viena
                būsena, o intensyvus protinis darbas – visai kita.
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline Flow */}
          <motion.div className="relative max-w-4xl mx-auto py-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-233" data-mp="1bm6xzrnfiulh">
            {/* Central Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-gold/60 via-gold/40 to-gold/20 -translate-x-1/2" data-id="element-234" data-mp="sxa25jcdvu15" />

            <div className="flex flex-col gap-16 md:gap-20 relative z-10" data-id="element-235" data-mp="xvldjyibuclh">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://cdn.magicpatterns.com/uploads/86D17L5NgjqReE3GCNF7d3/Screenshot_2026-03-06_at_13.44.55.png',
              imgStyle: {
                backgroundSize: '250%',
                backgroundPosition: '55% 35%'
              } as React.CSSProperties
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://cdn.magicpatterns.com/uploads/oYRvzX8HGPdzN3RmPEhdeg/zac-durant-_6HzPU9Hyfg-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plačių, erdvinių judesių iki tikslingo, koordinuotos veiklos',
              img: 'https://cdn.magicpatterns.com/uploads/6RbKwQ7msGyRimZEELWpys/atharva-tulsi-2jA_YoQuvaA-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://cdn.magicpatterns.com/uploads/deWv2jrD7JtPFbuwEysfBb/IMG_7871.jpg',
              imgStyle: {
                backgroundSize: '180%',
                backgroundPosition: '50% 40%'
              } as React.CSSProperties
            }].map((item, idx) => {
              const isEven = idx % 2 === 0;
              return <motion.div key={idx} variants={fadeInUp} className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-236" data-mp="31979g1mh0xq9" data-mpi={idx}>
                    {/* Mobile Dot */}
                    <div className="md:hidden absolute left-8 top-12 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-237" data-mp="3k5yq03amoyqm" />

                    {/* Desktop Center Dot */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-238" data-mp="1ou5v0475wpw5" />

                    {/* Image Side */}
                    <div className={`w-full md:w-1/2 flex pl-20 md:pl-0 ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} mb-5 md:mb-0`} data-id="element-239" data-mp="1f3uqaffnmco7">
                      <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0 shadow-xl" data-id="element-240" data-mp="27tjrotwa0eqc">
                        <div className="w-full h-full bg-cover bg-center" style={{
                      backgroundImage: `url('${item.img}')`,
                      ...item.imgStyle
                    }} data-id="element-241" data-mp="22sdyyw2m8rgt" />
                      </div>
                    </div>

                    {/* Text Side */}
                    <div className={`w-full md:w-1/2 flex flex-col pl-20 md:pl-0 ${isEven ? 'md:items-start md:pl-16' : 'md:items-end md:pr-16 md:text-right'}`} data-id="element-242" data-mp="igq1096s6sxg">
                      <h4 className="font-bold text-white mb-2 text-[20px]" data-id="element-243" data-mp="2y0y23mu21kai">
                        {item.title}
                      </h4>
                      <p className="text-sm text-white/60 leading-relaxed max-w-sm" data-id="element-244" data-mp="37u5dppfe1wj1">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-245" data-mp="3lqsa9r3fikof">
        <div className="max-w-5xl mx-auto" data-id="element-246" data-mp="3qqwspmnwdpha">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-247" data-mp="3qpvej9bl4mvc">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-248" data-mp="1rg5e6fkgg8wb">
              Rezultatai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-249" data-mp="1we6zz899mmbh" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-250" data-mp="182c5oyr1psyg">
              DFB Metodologijos Nauda
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-251" data-mp="1cyb136askb8d">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-252" data-mp="1rp9zxpltrler">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-253" data-mp="gaedvikumho6" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-254" data-mp="cro07uhcw96l" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-255" data-mp="mq4vv4r8pbyz" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-256" data-mp="3l8irecosk54u" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-257" data-mp="3v5wulalpexf7" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-258" data-mp="3v4lsrgbtjzqq">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-259" data-mp="32iklo38guo4d">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-260" data-mp="4sudm1q19pf3">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-261" data-mp="9sgucl52lnsn" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-262" data-mp="3d9p5yn97frex" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-263" data-mp="3n4qv3j3hcthi" data-mpi={i}>
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-264" data-mp="2mz6n2kqxh3ya" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-265" data-mp="2n0hqgjggo9ov">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-266" data-mp="2d8283l195j3g">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── (dark bg) */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-267" data-mp="1z816g3d5y7mx">
        <div className="max-w-5xl mx-auto" data-id="element-268" data-mp="1uaras5z6kzqn">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-269" data-mp="1pawjw2feoa5l">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-270" data-mp="1pcps3m5o32fp">
              DFB Metodika: Medituoti Lengva
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-271" data-mp="3jp5u2arfjuer" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-272" data-mp="ij4gqd73b4r">
              Prisijunk Prie Kurso
            </h2>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-273" data-mp="3mpzit9h4a36p">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '110€',
            label: 'už mėnesį',
            sub: 'Galimybė mokėti dalimis'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-274" data-mp="2kc4yee22i7j5" data-mpi={i}>
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-275" data-mp="2pe3z1g6faqce" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-276" data-mp="3d3evilgqpykq">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-277" data-mp="29t0ynt1dc2y9">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-278" data-mp="2ep99wvcg3c05">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-279" data-mp="2ogm2jp62i678" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-280" data-mp="2xhbswdck74dk">
            <motion.a href="https://www.instagram.com/vestirna/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2.5 whitespace-nowrap text-white px-10 py-[15px] rounded-full text-base font-semibold shadow-2xl transition-colors mb-5" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-281" data-mp="2xfr1gwljk1jx">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-282" data-mp="lvt7iyxsq6nb">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-283" data-mp="1jgxgxopqvady" />
              </svg>
              Parašyk mums žinutę
            </motion.a>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-284" data-mp="2cvjfg4ors0l2">
              <p className="text-sm text-white/50" data-id="element-285" data-mp="2gxdbfzo0sgew">
                Įdomu? Turi klausimų? Tiesiog parašyk ✨
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── (light bg) */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-286" data-mp="283rzeo38pd96">
        <div className="max-w-6xl mx-auto" data-id="element-287" data-mp="2vuxayr25djdo">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-288" data-mp="2vt2txntzybev">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-289" data-mp="30lm3m9pkcmee">
              3 etapai: išmok, patirk, pritaikyk
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-290" data-mp="39ycwj796n19z" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-291" data-mp="3erxk05d5gcux">
              Kurso Struktūra
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-292" data-mp="2r34vuv2rr9av">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1712342109846-a8fcb1c883ba?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1703945530393-600961291ced?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-293" data-mp="1tlymz6lhofo8">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-294" data-mp="3101rlx3fw87g" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-295" data-mp="3dfjdsx2o7o0i" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-296" data-mp="3j1vjdkkjfr">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-297" data-mp="itr0bo6bqlpm">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-298" data-mp="spzg5o65wh17">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-299" data-mp="nro73fnoyak1">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-300" data-mp="eq7ztgs6aok1">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-301" data-mp="eowu3bf636qa">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-302" data-mp="5054h9xkwyij">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-303" data-mp="3n5rp64pq3mtt">
        <div className="max-w-6xl mx-auto" data-id="element-304" data-mp="3i6orx6fzikwe">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-305" data-mp="3i7q63jsarnic">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-306" data-mp="1xow50ah5j92m">
              Tikros transformacijos istorijos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-307" data-mp="22lw8qeqj3zqk" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-308" data-mp="1eyo3txx0isp2">
              Dalyvių Atsiliepimai
            </h2>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Eglė V.',
            role: 'Kursų dalyvė',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus ir chaotiškas būsenas, bei juos keisti. Sugrįžti į čia ir dabar. Prisiimti atsakomybę už savo dėmesį. Nuosekliai kartojant pratimus ir darant praktikas įvyko pozityvus pokytis.'
          }, {
            name: 'Ieva M.',
            role: 'Kursų dalyvė',
            quote: 'Labiausiai patiko, kad 99% mokymų buvo patyrimas. Suvokimas visko per save. Būtent todėl DFB lengvai integravosi į mano gyvenimą. Perskaičiau ne vieną aprašymą apie DFB internete - visiškai neatspindi to, ką gauni. O gauni be galo daug. Sakyčiau, kad DFB - didelis asmeninis virsmas.'
          }, {
            name: 'Mantas R.',
            role: 'Kursų dalyvis',
            quote: 'Pagrindiniai įsisamoninimai ir suvokimai atėję nuoširdžiai praktikuojant ir naudojant DFB metodą yra: išmokimas, kaip keisti savo būsenas ir stebėti kaip per tai keičiasi ir aplinka, žmonės bei jų reakcijos; suvokimas, kaip įgalinti savo naudai visas būsenas neskirstant jų į geras ir blogas.'
          }, {
            name: 'Dovydas S.',
            role: 'Kursų dalyvis',
            quote: 'Labai aiškiai ir struktūruotai perteikiama kurso informacija, palydima per patyrimus. Isiklausymas į tai, kas kiekvienam aktualu, koks kiekvieno mokinio iššūkis, ir atsižvelgimas į tai. Rūpestis erdve, atmosfera, lankstumas situacijoms. Nuolatinis palaikymas, domėjimasis, įkvėpimas per konkrečius pavyzdžius. Dėkui už transformaciją ir nepakartojama pokytį.'
          }];
          const goToTestimonial = (idx: number) => {
            setTestimonialDirection(idx > activeTestimonial ? 1 : -1);
            setActiveTestimonial(idx);
          };
          const prevTestimonial = () => goToTestimonial(activeTestimonial === 0 ? testimonials.length - 1 : activeTestimonial - 1);
          const nextTestimonial = () => goToTestimonial(activeTestimonial === testimonials.length - 1 ? 0 : activeTestimonial + 1);
          const currentTestimonial = testimonials[activeTestimonial];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-309" data-mp="30a6oql9qqa8l">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-310" data-mp="2vcetbh9w05zn">
                  {/* Left arrow */}
                  <button onClick={prevTestimonial} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-311" data-mp="3ss2f0s2yrvkg">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-312" data-mp="94qvew1uuc9u">
                      <polyline points="15 18 9 12 15 6" data-id="element-313" data-mp="n2yupamd5po6" />
                    </svg>
                  </button>

                  {/* Testimonial content */}
                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-314" data-mp="wuuvwujqxvwm">
                    <AnimatePresence mode="wait" custom={testimonialDirection} data-id="element-315" data-mp="ww5zoud6z3nq">
                      <motion.div key={activeTestimonial} custom={testimonialDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-316" data-mp="11v93yclcl5un">
                        {/* Decorative quote mark */}
                        <span className="font-serif text-gold/25 select-none leading-none mb-2" style={{
                      fontSize: '6rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-317" data-mp="14dve74aohyrs">
                          &ldquo;
                        </span>

                        {/* Quote text — dynamically from current testimonial */}
                        <p className="text-mountain-darker text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-10 font-[400]" data-id="element-318" data-mp="ko1piz39fzrs">
                          {currentTestimonial.quote}
                        </p>

                        {/* Gold divider */}
                        <div className="w-12 h-px bg-gold mb-4" data-id="element-319" data-mp="2nee5hfmo0hg1" />

                        {/* Name & role */}
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-320" data-mp="2s9jn4291b9yb">
                          {currentTestimonial.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-321" data-mp="3k1shn0lads89">
                          {currentTestimonial.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Right arrow */}
                  <button onClick={nextTestimonial} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-322" data-mp="1unmaglhc5yua">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-323" data-mp="2i20j4s1lbjet">
                      <polyline points="9 18 15 12 9 6" data-id="element-324" data-mp="1ucpf91oy18wg" />
                    </svg>
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3 mt-10" data-id="element-325" data-mp="243amtemlhnri">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goToTestimonial(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeTestimonial ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeTestimonial ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-326" data-mp="16mbyw8fq0yhp" data-mpi={i} />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── (dark bg) */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-327" data-mp="2x63anqf928pb">
        <div className="max-w-4xl mx-auto" data-id="element-328" data-mp="2s78ivyzb19t2">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-329" data-mp="2n6umxfaz9qbe">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-330" data-mp="2n62qk29k3ok2">
              Patyrę instruktoriai ir praktikai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-331" data-mp="28kf4gbjsm9yk" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-332" data-mp="1zlaa7hf9l2s7">
              Mūsų Komanda
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-333" data-mp="2rawwsf0pr3je">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-334" data-mp="2mj51xsaiybng">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-335" data-mp="3a7xz1s2vupjn">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/p5KUhhvxDrMn2D53eKDHKt/agne3.jpg')",
                backgroundPosition: 'center 30%'
              }} data-id="element-336" data-mp="13kgvywdq1n3t" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-337" data-mp="18ebehwhlbw1b">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-338" data-mp="fnbahlj9es9n">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-339" data-mp="ulvhbcbdes8b" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-340" data-mp="pl7rgtyvohfo">
                DFB metodikos sertifikuota instruktorė, daugiau nei 10 metų
                meditacijų praktikė. Mokymuose ji padeda žmonėms sujungti
                meditaciją su kasdieniu gyvenimu – įnešti daugiau aiškumo,
                sąmoningo buvimo ir vidinės ramybės. Padeda dalyviams išmokti
                savireguliacijos ir lengvai pritaikyti meditacijos principus
                realiose gyvenimo situacijose.
              </p>
              <div className="relative max-w-xs" data-id="element-341" data-mp="jjflxkmh5vha">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-342" data-mp="ojadlqe2qp3e">
                  "
                </span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-343" data-mp="13akcfk3s2s5r">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-344" data-mp="1c9nv4mz195jq">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/cYk35xR7d8THHiA5ofpAZk/Screenshot_2026-03-06_at_13.56.34.png')",
                backgroundPosition: 'center 40%'
              }} data-id="element-345" data-mp="1rbwc90z1rl4e" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-346" data-mp="1de7tpzsgcq2i">
                Vestina Zablockytė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-347" data-mp="253au34h7xg9v">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-348" data-mp="20arj8f9x0z8r" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-349" data-mp="1wbsh12fkjgl0">
                „Sąmoningas judėjimas, šokis, rebefingas, Vipasana... Per 15
                metų leidausi į daugybę savęs pažinimo kelionių ir išbandžiau
                pačias įvairiausias praktikas. Tačiau būtent DFB metodika, kuria
                aktyviai gyvenu pastaruosius šešerius metus, tapo ta praktika,
                kuri iš esmės pakeitė mano kasdienybės kokybę. Šiandien noriu
                pasidalinti atradimu, kad medituoti lengva! Vietoj ilgų paieškų
                kviečiu iškart nerti į procesą, kuris atveria neįtikėtinai platų
                galimybių spektrą. Prisijunk!“
              </p>
              <div className="relative max-w-xs" data-id="element-350" data-mp="3dlsnuii7ebf">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-351" data-mp="3ctxgkstjilo">
                  "
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── (light bg) */}
      <section id="tvarkarastis" className="pt-24 pb-12 px-4 bg-sand-lightest" data-id="element-352" data-mp="1srs2yihr3ntm">
        <div className="max-w-6xl mx-auto" data-id="element-353" data-mp="3dac7fldciwuo">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-354" data-mp="3d918pzyrcfaf">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-355" data-mp="38dc2xew5a25o">
              Prisijunk prie rudens grupės
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-356" data-mp="3n3032cslaurt" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-357" data-mp="3rz8hfnyyqjxx">
              Artėjantis Kursas
            </h2>
          </motion.div>

          <CourseCalendar course={currentCourse} data-id="element-358" data-mp="4vanajkfrn4y" />

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12" {...fadeInUp} data-id="element-359" data-mp="4vtxfe3uh78f">
            <motion.button onClick={onViewCourses} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="inline-flex items-center gap-2 border border-mountain-darker/25 text-mountain-darker px-9 py-[13px] rounded-full text-base font-semibold bg-white" data-id="element-360" data-mp="26mx7o29tojsg">
              Kiti kursai
              <ArrowRightIcon className="w-4 h-4" data-id="element-361" data-mp="11vxdhbibmc9k" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── KONTAKTAI (4 variantų peržiūra) ── */}
      <ContactSection data-id="element-362" data-mp="1brs34qkyp7lu" />

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-363" data-mp="1bv672399xove">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-364" data-mp="1gt7m8d3kx41l">
          <div className="flex items-center gap-2" data-id="element-365" data-mp="2na2whe9deg3a">
            <Logo className="h-8" data-id="element-366" data-mp="2s60dznwddshn" />
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-367" data-mp="2s8wbjb4b2f5k">
            © 2026 tavo būsena. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-368" data-mp="bkv9xkj0emb7">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-369" data-mp="blevdb7vbstz">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-370" data-mp="1uagebi707z5">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-371" data-mp="3oxyhtkkaijiq">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}